
import { z } from "zod";
import { validatePostcode, coarsenPostcode } from './postcodeUtils';

// Profile schema
export const profileSchema = z.object({
  postcode: z.string().min(1, "Postcode is required"),
  property_type: z.string().min(1, "Property type is required"),
});

export type ProfileFormValues = z.infer<typeof profileSchema>;

// Job status schema
export const jobStatusSchema = z.enum([
  "todo",
  "quote_requested",
  "scheduled",
  "completed",
]);

// User job schema
export const userJobSchema = z.object({
  template_id: z.string().min(1, "Job template is required"),
  status: jobStatusSchema,
  last_done_date: z.string().optional(),
  next_due_date: z.string().optional(),
  notes: z.string().optional(),
});

export type UserJobFormValues = z.infer<typeof userJobSchema>;

// Tradesperson schema
export const tradespersonSchema = z.object({
  id: z.string().optional(),
  notes: z.string().optional(),
  preferred: z.boolean().default(false),
});

export type TradespersonFormValues = z.infer<typeof tradespersonSchema>;

// Auth schemas
export const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export type SignupFormValues = z.infer<typeof signupSchema>;

// Schema for TradesPurple onboarding
export const tradespurpleOnboardingSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be less than 20 characters")
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      "Username can only contain letters, numbers, underscores, and hyphens"
    ),
  postcode: z
    .string()
    .min(2, "Postal code must be at least 2 characters")
    .max(8, "Postal code is too long")
    .refine(
      (value) => validatePostcode(value),
      "Please enter a valid postal code (e.g. HP22, 90210, M4C 1B5)"
    )
    .transform((value) => coarsenPostcode(value)), // Coarsen for privacy
});

export type TradesPurpleOnboardingValues = z.infer<typeof tradespurpleOnboardingSchema>;
